import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { cn } from "@/lib/utils";
import {
  Plus,
  Trash2,
  Search,
  ArrowRightLeft,
  Calendar,
  Package,
  ArrowRight,
  ChevronRight,
  Info,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";

import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import RecordNotFound from "@/core/components/RecordNotFound";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BatchSelect = ({ availableBatches = [], value, onChange }) => {
  const options = availableBatches.map((b) => ({
    value: b.id,
    label: `${b.batch_number} (Available: ${b.quantity})`,
    available_quantity: b.available_quantity,
  }));

  return (
    <ReactSelect
      options={options}
      value={options.find((o) => o.value === value)}
      onChange={(val) =>
        onChange(val ? val.value : null, val ? val.available_quantity : 0)
      }
      placeholder="Select Batch"
      className="min-w-[200px]"
    />
  );
};

const SerialSelect = ({ availableSerials = [], value, onChange }) => {
  const options = availableSerials.map((s) => ({
    value: s.id,
    label: s.serial_number,
    status: s.status_info?.label,
    variant: s.status_info?.variant,
  }));

  return (
    <ReactSelect
      isMulti
      options={options}
      formatOptionLabel={(option) => (
        <div className="flex justify-between items-center w-full min-w-[120px]">
          <span className="font-mono text-xs">{option.label}</span>
          {option.status && (
            <Badge
              variant={option.variant || "secondary"}
              className="text-[9px] h-4 px-1"
            >
              {option.status}
            </Badge>
          )}
        </div>
      )}
      value={options.filter((o) => value.includes(o.value))}
      onChange={(selected) => {
        const selectedValues = selected ? selected.map((o) => o.value) : [];
        onChange(selectedValues);
      }}
      placeholder="Select Serials"
      className="min-w-[200px]"
    />
  );
};

const NewTransfer = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [formData, setFormData] = useState({
    source_store_id: "",
    destination_store_id: "",
    reference_no: "",
    transfer_date: new Date().toISOString().split("T")[0],
    notes: "",
    requires_approval: true,
  });

  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [productSearch]);

  const [confirmStoreChange, setConfirmStoreChange] = useState({
    isOpen: false,
    storeId: null,
  });

  // Fetch STORES
  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
    enabled: hasPermission("store read"),
  });

  // Product Search
  const { data: products = [], isFetching: isSearching } = useQuery({
    queryKey: ["all-pos-products", debouncedSearch, formData.source_store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 20,
          search: debouncedSearch || undefined,
          with: ["brand", "model", "unit", "categories"],
          manage_inventory: true,
          sellable: true,
          store_id: formData.source_store_id || undefined,
        },
      });

      return res.data.data || [];
    },
    enabled: !!formData.source_store_id && hasPermission("product read"),
    keepPreviousData: true,
  });

  const transferMutation = useMutation({
    mutationFn: async (payload) => {
      return apiClient.post(API_ENDPOINTS.STOCK.TRANSFERS, payload);
    },
    onSuccess: () => {
      toast.success("Stock transfer created successfully");
      queryClient.invalidateQueries({ queryKey: ["stockTransfers"] });
      navigate(all_routes.stocktransfer);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create stock transfer",
      );
    },
  });

  const handleSourceStoreChange = (storeId) => {
    if (items.length > 0 && storeId !== formData.source_store_id) {
      setConfirmStoreChange({ isOpen: true, storeId });
    } else {
      setFormData({ ...formData, source_store_id: storeId });
    }
  };

  const onConfirmStoreChange = () => {
    setItems([]);
    setFormData({ ...formData, source_store_id: confirmStoreChange.storeId });
    setConfirmStoreChange({ isOpen: false, storeId: null });
  };

  const handleAddProduct = (product) => {
    if (!formData.source_store_id) {
      toast.error("Please select a source store first");
      return;
    }

    if (items.find((item) => item.product_id === product.id)) {
      toast.error("Product already added");
      return;
    }

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      tracking_type: product.tracking_type || "none",
      inventory_batch_id: null,
      serial_numbers: [],
      quantity: 1,
      available_stock: product.sellable_qty ?? product.stock ?? 0,
      available_serials: product.serial_numbers || [],
      available_batches: product.batches || [],
    };

    setItems([...items, newItem]);
    setProductSearch("");
  };

  const updateItem = (index, updates) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };

    // Auto-update quantity for serial products
    if (updates.serial_numbers) {
      newItems[index].quantity = updates.serial_numbers.length;
    }

    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.source_store_id || !formData.destination_store_id) {
      toast.error("Please select source and destination stores");
      return;
    }

    if (formData.source_store_id === formData.destination_store_id) {
      toast.error("Source and destination stores must be different");
      return;
    }

    if (items.length === 0) {
      toast.error("Please add at least one item to transfer");
      return;
    }

    // Comprehensive item validation
    for (const item of items) {
      if (item.tracking_type === "standard" && !item.inventory_batch_id) {
        toast.error(`Please select a batch for ${item.product_name}`);
        return;
      }
      if (item.tracking_type === "serial" && item.serial_numbers.length === 0) {
        toast.error(`Please select serials for ${item.product_name}`);
        return;
      }
      if (item.quantity <= 0) {
        toast.error(`Quantity for ${item.product_name} must be at least 1`);
        return;
      }
      if (
        item.tracking_type !== "serial" &&
        item.quantity > item.available_stock
      ) {
        toast.error(
          `Quantity for ${item.product_name} exceeds available stock (${item.available_stock})`,
        );
        return;
      }
    }

    const payloadItems = [];
    items.forEach((item) => {
      if (
        item.tracking_type === "serial" &&
        item.serial_numbers &&
        item.serial_numbers.length > 0
      ) {
        item.serial_numbers.forEach((serialId) => {
          payloadItems.push({
            product_id: item.product_id,
            inventory_batch_id: null,
            inventory_serial_id: serialId,
            quantity: 1,
          });
        });
      } else {
        payloadItems.push({
          product_id: item.product_id,
          inventory_batch_id: item.inventory_batch_id || null,
          inventory_serial_id: null,
          quantity: item.quantity,
        });
      }
    });

    const payload = {
      source_store_id: formData.source_store_id,
      destination_store_id: formData.destination_store_id,
      reference_no: formData.reference_no,
      transfer_date: formData.transfer_date,
      notes: formData.notes,
      requires_approval: formData.requires_approval,
      items: payloadItems,
    };

    transferMutation.mutate(payload);
  };

  const storeOptions = stores.map((s) => ({ value: s.id, label: s.name }));
  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.sku})`,
    product: p,
  }));

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ArrowRightLeft className="h-6 w-6 text-primary" />
          </div>
          <PageHeaderHeading
            title="New Stock Transfer"
            subtitle="Create an inter-store stock movement"
          />
        </div>
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={() => navigate(all_routes.stocktransfer)}
            disabled={transferMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={transferMutation.isPending}>
            {transferMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Save Transfer"
            )}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Item Selection Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Transfer Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <ReactSelect
                  placeholder="Search products by name or SKU..."
                  onInputChange={(val) => setProductSearch(val)}
                  options={productOptions}
                  isLoading={isSearching}
                  onChange={(val) => val && handleAddProduct(val.product)}
                  value={null}
                  classNames={{
                    control: () => "!pl-8 min-h-[44px]",
                  }}
                />
              </div>

              <div className="rounded-xl border border-border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow className="hover:bg-transparent border-none">
                        <TableHead className="w-[45%] py-4">
                          Product Details
                        </TableHead>
                        <TableHead className="py-4">
                          Tracking Configuration
                        </TableHead>
                        <TableHead className="w-[140px] py-4">
                          Quantity
                        </TableHead>
                        <TableHead className="text-right py-4 px-6">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="h-32 text-center text-muted-foreground"
                          >
                            <div className="flex flex-col items-center gap-2">
                              <Package className="h-8 w-8 opacity-20" />
                              <p>
                                No items added yet. Search for products above.
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map((item, index) => (
                          <TableRow
                            key={index}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="py-4">
                              <div className="font-semibold text-foreground">
                                {item.product_name}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mt-1">
                                SKU: {item.sku}
                              </div>
                            </TableCell>
                            <TableCell className="py-4">
                              <div className="flex flex-col gap-2">
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    "w-fit capitalize font-medium",
                                    item.tracking_type === "none" &&
                                      "bg-slate-100 text-slate-600",
                                    item.tracking_type === "standard" &&
                                      "bg-blue-50 text-blue-600 border-blue-100",
                                    item.tracking_type === "serial" &&
                                      "bg-purple-50 text-purple-600 border-purple-100",
                                  )}
                                >
                                  {item.tracking_type} Tracking
                                </Badge>

                                {item.tracking_type === "standard" && (
                                  <div className="mt-1">
                                    <BatchSelect
                                      availableBatches={item.available_batches}
                                      value={item.inventory_batch_id}
                                      onChange={(batchId, available) =>
                                        updateItem(index, {
                                          inventory_batch_id: batchId,
                                          available_stock: available,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                                {item.tracking_type === "serial" && (
                                  <div className="mt-1">
                                    <SerialSelect
                                      availableSerials={item.available_serials}
                                      value={item.serial_numbers}
                                      onChange={(serials) =>
                                        updateItem(index, {
                                          serial_numbers: serials,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                                {item.tracking_type === "none" && (
                                  <p className="text-[10px] text-muted-foreground italic">
                                    Standard quantity-based inventory
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-4">
                              <div className="flex flex-col gap-1">
                                <Input
                                  type="number"
                                  min="1"
                                  max={item.available_stock}
                                  value={item.quantity}
                                  readOnly={item.tracking_type === "serial"}
                                  onChange={(e) =>
                                    updateItem(index, {
                                      quantity: parseInt(e.target.value) || 0,
                                    })
                                  }
                                  className={cn(
                                    "h-9 font-medium",
                                    item.tracking_type === "serial" &&
                                      "bg-muted cursor-not-allowed",
                                  )}
                                />
                                {item.tracking_type !== "serial" && (
                                  <p className="text-[10px] text-muted-foreground whitespace-nowrap">
                                    Available:{" "}
                                    <span className="font-bold text-foreground">
                                      {item.available_stock}
                                    </span>
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right py-4 px-6">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeItem(index)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Transfer Details Card */}
          <Card className="border-none shadow-sm bg-card/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2 font-bold">
                <div className="p-1.5 bg-green-500/10 rounded-md">
                  <Info className="h-4 w-4 text-green-600" />
                </div>
                Movement Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="source_store_id"
                  className="text-sm font-semibold"
                >
                  Source Store
                </Label>
                <ReactSelect
                  id="source_store_id"
                  options={storeOptions}
                  value={storeOptions.find(
                    (o) => o.value === formData.source_store_id,
                  )}
                  onChange={(val) => handleSourceStoreChange(val?.value || "")}
                  placeholder="Where is stock coming from?"
                />
              </div>

              <div className="flex justify-center -my-2">
                <div className="bg-muted p-1 rounded-full">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90 lg:rotate-0" />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="destination_store_id"
                  className="text-sm font-semibold"
                >
                  Destination Store
                </Label>
                <ReactSelect
                  id="destination_store_id"
                  options={storeOptions}
                  value={storeOptions.find(
                    (o) => o.value === formData.destination_store_id,
                  )}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      destination_store_id: val?.value || "",
                    })
                  }
                  placeholder="Where is stock going to?"
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label
                  htmlFor="transfer_date"
                  className="text-sm font-semibold"
                >
                  Transfer Date
                </Label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  </div>
                  <Input
                    id="transfer_date"
                    type="date"
                    className="pl-9 h-11"
                    value={formData.transfer_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        transfer_date: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reference_no" className="text-sm font-semibold">
                  Reference No{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    (Optional)
                  </span>
                </Label>
                <Input
                  id="reference_no"
                  value={formData.reference_no}
                  onChange={(e) =>
                    setFormData({ ...formData, reference_no: e.target.value })
                  }
                  placeholder="e.g. TRF/OUT/102"
                  className="h-11"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4 bg-background">
                <div className="space-y-0.5">
                  <Label className="text-sm font-semibold">
                    Requires Approval
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    If enabled, this transfer will be sent for manager approval
                    before processing.
                  </p>
                </div>
                <Switch
                  checked={formData.requires_approval}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, requires_approval: checked })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-semibold">
                  Additional Notes
                </Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Describe why this transfer is happening..."
                  className="min-h-[100px] resize-none"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <AlertDialog
        open={confirmStoreChange.isOpen}
        onOpenChange={(isOpen) =>
          setConfirmStoreChange((prev) => ({ ...prev, isOpen }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Source Store?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing the source store will clear all items already added to
              the transfer list. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmStoreChange}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default NewTransfer;
